// feeRules.ts
// -----------
// Pure-function business logic for the Fees page. Sprint 5 / story 5.2a.
//
// Why this file exists: the same calculations were inlined three different
// ways in Fees.tsx — once in the unit-summary table, once in the simplified
// fees-by-type table, once in the late-fee policy preview. Inlined logic
// can't be tested without rendering the whole page, can't be reused by the
// Reports tab, and tends to drift between sites.
//
// Rules: NO React, NO DOM, NO apiService, NO Zustand. Plain data in, plain
// data out. Every function in this file gets a unit test in
// `__tests__/feeRules.test.ts`.

import { Fee, LateFeePolicy, LateFeeCalculationType, Owner } from '../../types';
import { currency } from '../../utils/currency';

// ── Status helpers ──────────────────────────────────────────────────────────

export type FeeStatus = Fee['status'];

/**
 * Status → display label + Antd color. Centralised so the same status
 * never renders as "OVERDUE" red here and "Past due" orange there.
 */
export const STATUS_DISPLAY: Record<FeeStatus, { color: string; label: string }> = {
  PAID:    { color: 'green',  label: 'PAID' },
  PENDING: { color: 'orange', label: 'PENDING' },
  OVERDUE: { color: 'red',    label: 'OVERDUE' },
  WAIVED:  { color: 'purple', label: 'WAIVED' },
};

export function statusDisplay(status: FeeStatus | null | undefined): { color: string; label: string } {
  if (!status || !STATUS_DISPLAY[status]) {
    return { color: 'purple', label: 'Unknown' };
  }
  return STATUS_DISPLAY[status];
}

// ── Currency formatting ─────────────────────────────────────────────────────

/**
 * Money rendering for the fee surfaces. Two decimals, leading `$`.
 *
 * Sprint 138 (ADR-133): delegates to the one `currency()` helper. This used to
 * be a hand-rolled dollar-sign + two-decimal template that called itself the
 * single source of truth — while `reports/reportRules.formatCurrency` claimed
 * the same title with different formatting (and no thousands separators here,
 * so a $12,500 special assessment rendered as `$12500.00`).
 */
export const formatCurrency = currency;

// ── Per-unit summaries ──────────────────────────────────────────────────────

export interface UnitFeeSummary {
  unitNumber: string;
  ownerName:  string;
  /**
   * The key fees are matched on: the owner's login-user UUID, or the numeric
   * owner-row id for an owner with no login. NOT unique — see `rowKey`.
   */
  ownerId:    string;
  /**
   * Sprint 139 — the owner ROW id (`owners.id`, a PK, so unique by construction).
   * This, not `ownerId`, identifies a row: two owner rows sharing a `user_id`
   * would collide on `ownerId` and one unit would vanish from the manager's view.
   */
  rowKey:     string;
  totalFees:    number;
  paidFees:     number;
  pendingFees:  number;
  overdueFees:  number;
  fees: Fee[];
}

/**
 * Walk every owner once, then bucket every fee against them.
 *
 * Sprint 135 (R12-2, ADR-130) — **the condo-wide fan-out happens on the SERVER.**
 * `FeeService.createCondoWideFee` writes one real row PER OWNER, each carrying
 * that owner's `ownerId` and its own `status`, all sharing a `groupId`. Every one
 * of those rows also carries `isCondoWide: true` — which is *provenance*, not an
 * instruction to broadcast.
 *
 * This function used to branch on `fee.isCondoWide` and copy such a row into
 * EVERY summary, re-fanning an already-fanned batch. With N owners that inflated
 * each owner's total N× and, worse, credited one owner's payment to everyone —
 * the R12-2 "$1500.00 Paid · All Paid" shown to owners who had paid nothing.
 *
 * So the ONLY row that still fans out here is the ownerless one: the fallback
 * `createCondoWideFee` saves when no owner has a login (`ownerId = null`). It has
 * no owner to bucket into, so each summary gets a copy keyed with a synthetic
 * `<feeId>-<ownerId>` id to keep React keys unique.
 *
 * Pure: takes the inputs as parameters, doesn't read the store.
 *
 * Sprint 139 (R12-11) — this map is keyed on the owner ROW id, not on the
 * fee-matching key. It used to key on `userId ?? id` with a last-write-wins
 * `map.set`, so two owner rows sharing a `user_id` would collapse: one unit
 * disappeared from the manager's fee view with no error, no warning, nothing
 * on screen to suggest a unit was missing. A silently short table is the worst
 * possible failure mode for a page a manager reconciles money against.
 *
 * That state is unreachable through the app's own write paths (owners is UNIQUE
 * on (condo_id, email) and OwnerService derives user_id from the unique
 * apps.users.email; V45 now enforces it at the DB too) — this is defence in
 * depth, not a live bug. But "unreachable" is a claim about today's code, and
 * the cost of being wrong was a wrong number with no symptom.
 */
export function computeUnitFeeSummaries(owners: Owner[], fees: Fee[]): UnitFeeSummary[] {
  const map = new Map<string, UnitFeeSummary>();
  // fee.ownerId → the summaries claiming that key. A well-formed condo has
  // exactly one summary per key; more than one is the collision described above.
  const byFeeKey = new Map<string, UnitFeeSummary[]>();

  owners.forEach((o) => {
    // fee.ownerId is the user UUID, which lives on Owner as `userId`. Older
    // Owner rows lack it — fall back to the numeric id so we never silently
    // drop an owner from the table.
    const feeKey = o.userId ?? String(o.id);
    const rowKey = String(o.id);
    const summary: UnitFeeSummary = {
      unitNumber: o.unitNumber,
      ownerName:  o.name,
      ownerId:    feeKey,
      rowKey,
      totalFees:    0,
      paidFees:     0,
      pendingFees:  0,
      overdueFees:  0,
      fees: [],
    };
    map.set(rowKey, summary);
    const claimants = byFeeKey.get(feeKey);
    if (claimants) claimants.push(summary);
    else byFeeKey.set(feeKey, [summary]);
  });

  const collisions = findDuplicateOwnerKeys(owners);
  if (collisions.length > 0) {
    // Loud, not fatal: every unit still renders (that's the point of rowKey);
    // only the ATTRIBUTION of a fee between the colliding units is ambiguous,
    // and we resolve it deterministically below rather than arbitrarily.
    // eslint-disable-next-line no-console
    console.warn(
      '[feeRules] Duplicate owner fee-keys in this condo — fee attribution between ' +
      'the affected units is ambiguous and their totals may be wrong. ' +
      'Expected to be impossible; investigate the owner rows. Keys: ' +
      collisions.join(', '),
    );
  }

  fees.forEach((fee) => {
    // NOTE: `!fee.ownerId` only — deliberately NOT `fee.isCondoWide || !fee.ownerId`.
    // A fanned-out condo-wide row is owner-bearing and belongs to exactly one
    // owner, like any other fee. See the header comment.
    if (!fee.ownerId) {
      // Ownerless fallback row: no owner to attribute it to, so show it on every unit.
      map.forEach((summary) => {
        summary.fees.push({ ...fee, id: `${fee.id}-${summary.ownerId}` });
        applyAmount(summary, fee);
      });
    } else {
      // On a collision, attribute to the first claimant only. Attributing to all
      // would double-count the money; picking arbitrarily would be the old bug
      // wearing a different hat. First-claimant is at least stable across renders,
      // and the console.warn above says the number is not to be trusted.
      const summary = byFeeKey.get(fee.ownerId)?.[0];
      if (summary) {
        summary.fees.push(fee);
        applyAmount(summary, fee);
      }
    }
  });

  return Array.from(map.values());
}

/**
 * The fee-keys claimed by more than one owner row. Empty for every well-formed
 * condo; see `computeUnitFeeSummaries` for why a non-empty result is a data bug
 * rather than a state the UI should try to render its way out of.
 *
 * Exported so a caller can surface the condition instead of only logging it.
 */
export function findDuplicateOwnerKeys(owners: Owner[]): string[] {
  const counts = new Map<string, number>();
  owners.forEach((o) => {
    const feeKey = o.userId ?? String(o.id);
    counts.set(feeKey, (counts.get(feeKey) ?? 0) + 1);
  });
  return Array.from(counts.entries())
    .filter(([, n]) => n > 1)
    .map(([key]) => key);
}

function applyAmount(summary: UnitFeeSummary, fee: Fee): void {
  summary.totalFees += fee.amount;
  if (fee.status === 'PAID')    summary.paidFees    += fee.amount;
  if (fee.status === 'PENDING') summary.pendingFees += fee.amount;
  if (fee.status === 'OVERDUE') summary.overdueFees += fee.amount;
}

// ── Unique-types view ──────────────────────────────────────────────────────

export interface FeeTypeSummary {
  type: string;
  amount: number;
  originalFee: Fee;
  isCondoWide: boolean;
  totalUnits: number;
  status: FeeStatus;
  /** Sprint 135 — set for a server-fanned batch; identifies the whole assessment. */
  groupId?: string;
  /** Sprint 135 — how many owners in the batch have settled their share. */
  paidUnits: number;
}

/**
 * The Fee Management catalogue: one line per *charge the manager created*.
 *
 * Sprint 135 (R12-2, ADR-130) — this used to dedupe on `fee.type` and take the
 * FIRST matching row's amount and status as the whole line's. Two problems:
 * a server-fanned condo-wide batch is N rows of one type, so N-1 rows were
 * silently dropped and one arbitrary owner's status stood for everyone; and two
 * genuinely different assessments of the same type (say two special assessments
 * in different periods) collapsed into a single line.
 *
 * Now: fanned rows are grouped by their `groupId` — the batch IS the line — and
 * the line reports a real "x of N paid" rollup. Ordinary per-owner fees keep the
 * previous per-type dedupe so that view is unchanged.
 */
/**
 * The identity of one catalogue LINE — a server-fanned batch, or a fee type.
 *
 * Sprint 141 (R13-2) — exported so the table's React `rowKey` and this function's
 * dedup map share ONE definition. Sprint 135 changed a line from "one per type"
 * to "one per batch" but `AllFeesTable` still declared `rowKey="type"`; two
 * assessments of the same type became two legitimate rows with identical keys,
 * which React reports as a duplicate-key error and can render incorrectly. The
 * data shape moved and the table key didn't follow — so neither should own the
 * rule alone.
 */
export function feeTypeRowKey(row: { groupId?: string; type: string }): string {
  return row.groupId ? `group:${row.groupId}` : `type:${row.type}`;
}

export function getUniqueFeeTypes(fees: Fee[], owners: Owner[]): FeeTypeSummary[] {
  const seen = new Map<string, FeeTypeSummary>();

  fees.forEach((fee) => {
    // A batch is keyed by groupId; anything else keeps the legacy per-type key.
    const batched = !!fee.groupId;
    const key = feeTypeRowKey(fee);

    const existing = seen.get(key);
    if (existing) {
      if (batched) {
        // Another owner's share of the SAME assessment: count the unit, don't
        // re-add the amount (the line shows unit price × units).
        existing.totalUnits += 1;
        if (fee.status === 'PAID') existing.paidUnits += 1;
      }
      return;
    }

    // `isCondoWide` is provenance; an ownerless row is condo-wide by construction.
    const condoWide = fee.isCondoWide || !fee.ownerId;
    seen.set(key, {
      type: fee.type,
      amount: fee.amount,
      originalFee: fee,
      isCondoWide: condoWide,
      // A batch counts its own rows as we walk them (starting at this one). The
      // ownerless fallback still notionally applies to every owner.
      totalUnits: batched ? 1 : (condoWide ? owners.length : 1),
      status: fee.status,
      groupId: fee.groupId,
      paidUnits: fee.status === 'PAID' ? 1 : 0,
    });
  });

  return Array.from(seen.values());
}

// ── Synthetic IDs (condo-wide rows) ────────────────────────────────────────

/**
 * Condo-wide fees get rendered once per owner with a synthetic id of the
 * form `<realFeeId>-<ownerId>` so React keys don't collide. Strip the
 * suffix back off before calling any backend endpoint that expects a UUID.
 *
 * UUIDs are 36 characters. Anything longer is a synthetic composite.
 */
export function realFeeId(maybeSynthetic: string): string {
  return maybeSynthetic.length > 36 ? maybeSynthetic.substring(0, 36) : maybeSynthetic;
}

// ── Permissions ─────────────────────────────────────────────────────────────

export type ManageRole = 'super_admin_condo' | 'admin' | 'owner' | 'board-member' | string | undefined;

export function canManageFees(role: ManageRole): boolean {
  return role === 'super_admin_condo' || role === 'admin';
}

export function canWaiveFee(fee: Fee): boolean {
  // PAID fees are immutable — the backend rejects waiver attempts on them.
  // Re-export the rule client-side so the menu can hide the option.
  return fee.status !== 'PAID';
}

// ── Late-fee policy math (S17 logic, lifted out for testing) ───────────────

export interface LateFeeQuote {
  appliedAmount: number;
  /** True iff a cap was hit and the quote was clamped. */
  capped: boolean;
}

/**
 * Compute the late fee that would be added to an overdue fee, given a policy.
 * Pure mirror of the backend's S17 LateFeeEngine — useful for the dry-run
 * preview in the SPA.
 *
 *   - FLAT: a fixed dollar amount, regardless of overdue size.
 *   - PERCENTAGE: a percentage of the overdue principal, optionally capped.
 *   - COMPOUNDING: percentage applied per overdue period; here we treat one
 *     period and let the caller iterate. Compounding-with-period logic
 *     belongs server-side; this is the single-period quote.
 *
 * Returns 0 / capped:false when the policy is inactive or the inputs are
 * missing — never throws, never returns NaN.
 */
export function quoteLateFee(
  overdueAmount: number,
  policy: LateFeePolicy | null | undefined,
): LateFeeQuote {
  if (!policy || !policy.isActive) return { appliedAmount: 0, capped: false };
  if (overdueAmount == null || overdueAmount <= 0) return { appliedAmount: 0, capped: false };

  const cap = policy.maxCap ?? Number.POSITIVE_INFINITY;

  let raw = 0;
  switch (policy.calculationType as LateFeeCalculationType) {
    case 'FLAT':
      raw = policy.flatAmount ?? 0;
      break;
    case 'PERCENTAGE':
    case 'COMPOUNDING':
      raw = (overdueAmount * (policy.percentageRate ?? 0)) / 100;
      break;
    default:
      raw = 0;
  }

  const applied = Math.min(raw, cap);
  return {
    appliedAmount: round2(applied),
    capped: raw > cap,
  };
}

/** Round to cents, half-away-from-zero — keeps display deterministic.
 *
 *  Sign is handled explicitly: Math.round rounds .5 toward +Infinity
 *  (banker's-rounding-ish for negatives), so `Math.round(-100.5) === -100`,
 *  not -101. We strip the sign first so half-away-from-zero is symmetric
 *  for both polarities. The Number.EPSILON nudge corrects the binary
 *  float representation of values like 1.005 (stored as 1.00499...).
 */
export function round2(x: number): number {
  const sign = x < 0 ? -1 : 1;
  return sign * Math.round((Math.abs(x) + Number.EPSILON) * 100) / 100;
}
